/**
 * Typed errors for the two other 409 conflicts the rights-profiles API can raise
 * besides "profile in use" (see `profile-in-use.error.ts`). The backend returns
 * all of them as HTTP 409 with distinct message strings and no structured
 * discriminator, so the API client classifies them by message keyword (option A,
 * frontend-only). Each is displayable as-is by the caller.
 */

/**
 * A profile name is already taken (`POST`/`PATCH`) or reserved ("Super Admin").
 * `profileName` is filled when the backend message carries it, else `undefined`.
 */
export class ProfileNameConflictError extends Error {
  readonly isProfileNameConflict = true;
  readonly profileName?: string;

  constructor(profileName?: string) {
    super(
      profileName
        ? `A rights profile named "${profileName}" already exists`
        : 'A rights profile with this name already exists',
    );
    this.name = 'ProfileNameConflictError';
    this.profileName = profileName;
    Object.setPrototypeOf(this, ProfileNameConflictError.prototype);
  }

  static is(error: unknown): error is ProfileNameConflictError {
    return error instanceof ProfileNameConflictError;
  }
}

/** A locked (system) profile cannot be modified or deleted. */
export class ProfileLockedError extends Error {
  readonly isProfileLocked = true;

  constructor() {
    super('This rights profile is locked and cannot be modified or deleted');
    this.name = 'ProfileLockedError';
    Object.setPrototypeOf(this, ProfileLockedError.prototype);
  }

  static is(error: unknown): error is ProfileLockedError {
    return error instanceof ProfileLockedError;
  }
}

/**
 * Classifies a backend 409 `ConflictException` message into a typed conflict error,
 * or `null` when it is none of these two (the caller then handles the remaining
 * case — e.g. "profile in use" on a DELETE). Reads the message only to pick the
 * TYPE; it never extracts data the caller already holds (the profile name on a
 * name conflict is a convenience, filled only when present).
 */
export function classifyConflict(
  message: string | undefined,
): ProfileLockedError | ProfileNameConflictError | null {
  if (!message) return null;
  if (/locked/i.test(message)) return new ProfileLockedError();
  if (/already exists/i.test(message)) {
    const name = message.match(/named "([^"]+)"/i)?.[1];
    return new ProfileNameConflictError(name);
  }
  if (/reserved/i.test(message)) return new ProfileNameConflictError();
  return null;
}
